<!--
  线性零部件阵列引用选择列表。

  文件定位：
  - 封装阵列方向、参考几何、所选参考等引用类输入；
  - 使用 SelectionListFrame 保持与“所选实体”列表一致的右键删除体验；
  - 不直接访问 cad-core，所有交互通过事件交给父组件处理。
-->
<template>
  <div
    class="reference-list"
    @click="handleActivate"
  >
    <SelectionListFrame
      :class="{
        'selection-list-frame--single-row': isSingleRow,
        'selection-list-frame--active': active,
      }"
      :items="items"
      :active-index="active ? 0 : -1"
      list-role="list"
      :visible-rows="visibleRows"
      item-key-field="objectId"
      :empty-text="emptyText"
      :context-menu-enabled="true"
      delete-label="消除选中"
      delete-all-label="消除选中"
      :show-delete-all-action="props.showDeleteAllAction"
      @update:active-index="handleActiveIndexChange"
      @delete-item="handleDeleteItem"
      @delete-all="handleDeleteAll"
    >
      <template #item="{ item }">
        <div class="reference-item">
          <n-text>{{ resolveItemLabel(item) }}</n-text>
        </div>
      </template>
    </SelectionListFrame>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { NText } from 'naive-ui';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import type { LinearComponentPatternReferenceItem } from '@/core/property-manager/linear-component-pattern/LinearComponentPatternPropertyTypes';

/** 组件属性。 */
interface LinearComponentPatternReferenceListProps {
  /** 引用项列表。 */
  items: LinearComponentPatternReferenceItem[];
  /** 当前列表是否为激活输入。 */
  active: boolean;
  /** 空列表提示。 */
  emptyText: string;
  /** 可见行数。 */
  visibleRows?: number;
  /** 是否显示“删除全部”右键动作。 */
  showDeleteAllAction?: boolean;
}

const props = withDefaults(defineProps<LinearComponentPatternReferenceListProps>(), {
  showDeleteAllAction: true,
});

/**
 * 单行显示标记。
 *
 * 说明：当可见行数为 1 时，内部列表框固定为单行输入外观，
 * 需要隐藏纵向滚动条以保持与 SOLIDWORKS PropertyManager 单字段一致的视觉密度。
 */
const isSingleRow = computed<boolean>(() => {
  const rows = Number(props.visibleRows ?? 2);
  return Number.isFinite(rows) && Math.round(rows) <= 1;
});

const emit = defineEmits<{
  /** 激活当前引用输入。 */
  (event: 'activate'): void;
  /** 删除指定引用项。 */
  (event: 'delete-item', payload: { item: LinearComponentPatternReferenceItem; index: number }): void;
  (event: 'delete-all'): void;
}>();

/**
 * 点击列表时激活当前引用输入。
 */
function handleActivate(): void {
  emit('activate');
}

/**
 * 列表激活项变化时保持当前引用输入激活。
 */
function handleActiveIndexChange(): void {
  emit('activate');
}

/**
 * 处理删除单项。
 *
 * @param payload 删除载荷。
 */
function handleDeleteItem(payload: { item: unknown; index: number }): void {
  emit('delete-item', {
    item: payload.item as LinearComponentPatternReferenceItem,
    index: payload.index,
  });
}

/**
 * 处理删除全部。
 */
function handleDeleteAll(): void {
  emit('delete-all');
}

/**
 * 解析引用项显示名称。
 *
 * @param item 引用项。
 * @returns 显示名称。
 */
function resolveItemLabel(item: unknown): string {
  const referenceItem = item as LinearComponentPatternReferenceItem;
  return referenceItem.label || referenceItem.objectId;
}
</script>

<style scoped lang="less">
.reference-list {
  min-width: 0;
}

.reference-list :deep(.selection-list-frame--single-row) {
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.reference-list :deep(.selection-list-frame--single-row::-webkit-scrollbar) {
  width: 0;
  height: 0;
}

.reference-list :deep(.selection-list-frame--active) {
  border-color: #2f8cff;
  background: rgba(47, 140, 255, 0.08);
}

.reference-item {
  display: flex;
  min-width: 0;
  align-items: center;
}
</style>
